import type { IconProps } from '@carboncopyco/icons';
import classNames from 'classnames';
import React from 'react';
import type { BgColors, Colors } from '../../constants';
import { TextBoxEditorTitle } from '../Typography';

export type PillVariant =
  | 'primary'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'red'
  | 'dark'
  | 'grey'
  | 'disabled';

export interface PillProps {
  variant: PillVariant;
  label: string;
  leftIcon?: React.ReactElement<IconProps>;
  rightIcon?: React.ReactElement<IconProps>;
}

const pillStyles: Record<
  PillVariant,
  { textColor: Colors; backgroundColor: BgColors }
> = {
  primary: {
    textColor: 'text-ds-primary-300',
    backgroundColor: 'bg-ds-natural-100',
  },
  blue: {
    textColor: 'text-ds-primary-500',
    backgroundColor: 'bg-ds-primary-100',
  },
  green: {
    textColor: 'text-ds-accent2-500',
    backgroundColor: 'bg-ds-accent2-100',
  },
  yellow: {
    textColor: 'text-ds-secondary-500',
    backgroundColor: 'bg-ds-secondary-100',
  },
  red: {
    textColor: 'text-ds-danger-500',
    backgroundColor: 'bg-ds-danger-100',
  },
  dark: {
    textColor: 'text-ds-accent3-200',
    backgroundColor: 'bg-ds-natural-600',
  },
  grey: {
    textColor: 'text-ds-natural-500',
    backgroundColor: 'bg-ds-natural-50',
  },
  disabled: {
    textColor: 'text-ds-natural-200',
    backgroundColor: 'bg-ds-natural-200-a',
  },
};

export const Pill = ({ variant, label, leftIcon, rightIcon }: PillProps) => {
  const { textColor, backgroundColor } = pillStyles[variant];

  return (
    <div
      className={classNames([
        backgroundColor,
        'flex items-center px-1.5 rounded-ds-40',
      ])}
    >
      {leftIcon && (
        <div className={classNames([textColor, 'flex items-center px-0.5'])}>
          {React.cloneElement(leftIcon, { size: 11 })}
        </div>
      )}
      <TextBoxEditorTitle
        uppercase
        color={textColor}
        label={label}
      />
      {rightIcon && (
        <div className={classNames([textColor, 'flex items-center px-0.5'])}>
          {React.cloneElement(rightIcon, { size: 11 })}
        </div>
      )}
    </div>
  );
};
